import { Data } from 'effect';
import type { ProxyTypeEnum, SubjectRangeAnalysis, ProxyMeta } from 'venn-api';
import type { SelectedProxy } from '../utils';
import type { SecondLayerProxyType, FirstLayerProxyTypeValue, SecondLayerProxyTypeValue } from 'venn-utils';
import type { LayeredProxyConfiguration } from '../proxy-types';
import type { FundToBulkProxy, FundToProxy } from '../../types';
import type { BulkProxyValidationState } from '../useProxyErrorMessage';
import type { LayeredDetailedProxyMetadata } from '../../../../hooks/useProxyDataQuery';

export type ProxyTabValue = 'first' | 'second';

export type ExtrapolationToggleState =
  | {
      state: 'hidden';
      isExtrapolated: boolean;
      reason: string;
    }
  | {
      state: 'disabled';
      isExtrapolated: boolean;
      reason: string;
    }
  | {
      state: 'enabled';
      isExtrapolated: boolean;
      reason?: undefined;
    };

export interface ProxyPickerStateBase {
  selectedProxyType: ProxyTypeEnum | undefined;
  selectedProxy: SelectedProxy | null;
  numLags: number;
  shouldExtrapolate: boolean;
  extrapolationToggleState: ExtrapolationToggleState;
}

export interface LegacyState extends ProxyPickerStateBase {}

export interface LayeredState {
  configuration: LayeredProxyConfiguration;
  activeTab: ProxyTabValue;
}

export interface LegacyStateMutations {
  setProxyType: (type: ProxyTypeEnum) => void;
  setProxy: (proxy: SelectedProxy | null) => void;
  setNumLags: (lags: number) => void;
  setExtrapolate: (extrapolate: boolean) => void;
}

export interface LayeredStateMutations {
  setActiveTab: (tab: ProxyTabValue) => void;
  setFirstLayerProxyType: (type: ProxyTypeEnum) => void;
  setFirstLayerProxy: (proxy: SelectedProxy | null) => void;
  setFirstLayerNumLags: (lags: number) => void;
  setFirstLayerExtrapolate: (extrapolate: boolean) => void;
  setSecondLayerProxyType: (type: SecondLayerProxyType, extrapolate: boolean) => void;
  removeSecondLayer: () => void;
  setSecondLayerProxy: (proxy: SelectedProxy | null) => void;
  setSecondLayerNumLags: (lags: number) => void;
  setSecondLayerExtrapolate: (extrapolate: boolean) => void;
}

export type ProxyPickerStateResult = Data.TaggedEnum<{
  Legacy: {
    state: LegacyState;
    mutations: LegacyStateMutations;
  };
  Layered: {
    state: LayeredState;
    mutations: LayeredStateMutations;
  };
}>;
export const ProxyPickerStateResult = Data.taggedEnum<ProxyPickerStateResult>();

export type LegacyProxyPickerStateResult = ReturnType<typeof ProxyPickerStateResult.Legacy>;
export type LayeredProxyPickerStateResult = ReturnType<typeof ProxyPickerStateResult.Layered>;

export type ProxyPickerAction = Data.TaggedEnum<{
  Save: {};
  Remove: {};
  SelectProxyType: { value: FirstLayerProxyTypeValue };
  SelectSecondLayerProxyType: { value: SecondLayerProxyTypeValue };
  SelectProxy: { proxy: SelectedProxy | null };
  SetNumLags: { lags: number };
  SetExtrapolate: { extrapolate: boolean };
  RemoveSecondLayer: {};
  ChangeTab: { tab: ProxyTabValue };
}>;
export const ProxyPickerAction = Data.taggedEnum<ProxyPickerAction>();

export interface UseProxyDataOptions {
  investments: (FundToProxy | FundToBulkProxy)[];
  proxySetId: string | undefined;
}

export interface UseProxyDataResult {
  returnsProxyDataByFund: Record<string, LayeredDetailedProxyMetadata>;
  rangeDataByFund: Record<string, SubjectRangeAnalysis | undefined>;
}

export interface UseProxyPickerStateOptions {
  investments: (FundToProxy | FundToBulkProxy)[];
  proxySetId: string | undefined;
  returnsProxyDataByFund: Record<string, LayeredDetailedProxyMetadata>;
}

interface BaseState {
  investments: (FundToProxy | FundToBulkProxy)[];
  proxySetId: string | undefined;
  validationState: BulkProxyValidationState;
  hasSingleProxy: boolean;
  isResetOperation: boolean;
}

interface BaseMutations {
  onProxyChange:
    | ((
        appliedProxies: (ProxyMeta & { proxyName: string })[],
        failedToProxyFunds: (FundToProxy | FundToBulkProxy)[],
      ) => void)
    | undefined;
  onClose: () => void;
  setIsUpdating: (isUpdating: boolean) => void;
}

type LayeredStateForActions = Pick<LayeredState, 'configuration' | 'activeTab'>;

export type ProxyPickerActionsDependencies = Data.TaggedEnum<{
  Legacy: {
    state: BaseState & { proxy: ProxyPickerStateBase };
    mutations: BaseMutations & { proxy: LegacyStateMutations };
  };
  Layered: {
    state: BaseState & { proxy: LayeredStateForActions };
    mutations: BaseMutations & { proxy: LayeredStateMutations };
  };
}>;
export const ProxyPickerActionsDependencies = Data.taggedEnum<ProxyPickerActionsDependencies>();
